/**
 * Double Progression System
 *
 * Instead of jumping weight when RPE is low, first increase reps within a range,
 * THEN increase weight and reset reps.
 *
 * Example: Rep range 8-12
 * - Session 1: 100 lbs × 8 reps (RPE 7) → increase to 9 reps
 * - Session 2: 100 lbs × 9 reps (RPE 7) → increase to 10 reps
 * - ...
 * - Session 5: 100 lbs × 12 reps (RPE ≤ 8) → increase weight to 105 lbs, reset to 8 reps
 */

import { db } from './db';
import { getCurrentUserId } from './anonymousAuth';
import { roundToNearestPlate } from './plateMath';
import { isCompoundMovement } from './exerciseDatabase';

// ============================================================================
// TYPES
// ============================================================================

export interface DoubleProgressionState {
  oderId: string;        // canonical exercise ID (compound key with userId)
  odexieId?: string;     // Dexie auto-generated id
  userId: string;
  currentReps: number;   // where we are in the rep range
  targetRepMin: number;  // e.g., 8
  targetRepMax: number;  // e.g., 12
  currentWeight: number;
  lastUpdated: string;   // ISO date
}

export interface DoubleProgressionResult {
  newWeight: number;
  newTargetReps: number;
  action: 'hold' | 'increase-reps' | 'increase-weight';
  reason: string;
}

// ============================================================================
// CORE LOGIC
// ============================================================================

/**
 * Evaluate double progression for an exercise based on last session performance.
 *
 * Rules:
 * 1. All sets hit targetRepMax at RPE ≤ 8 → increase weight, reset reps to targetRepMin
 * 2. All sets hit current target at RPE ≤ 8 → increase target reps by 1
 * 3. Otherwise → hold
 */
export function evaluateDoubleProgression(
  state: DoubleProgressionState,
  completedReps: number[],  // reps per set from last session
  avgRPE: number
): DoubleProgressionResult {
  // No sets completed — hold
  if (completedReps.length === 0) {
    return {
      newWeight: state.currentWeight,
      newTargetReps: state.currentReps,
      action: 'hold',
      reason: 'No completed sets',
    };
  }

  const allSetsHitTarget = completedReps.every(r => r >= state.currentReps);
  const allSetsHitMax = completedReps.every(r => r >= state.targetRepMax);
  const rpeAllowsProgress = avgRPE <= 8;

  // Rule 1: Topped out rep range → increase weight
  if (allSetsHitMax && rpeAllowsProgress) {
    /**
     * `oderId` is a COMPOSITE Dexie key, `${userId}_${exerciseName}` (see the
     * writer below) — not an exercise name. Passing it straight to
     * isCompoundMovement meant the lookup received something like
     * "kkQXifx3c8USP2n9s22SN5UbePz1_back squat" and never matched, so
     * `isCompound` was false 100% of the time and every lift progressed at
     * the ISOLATION increment: a back squat moved 2.5% per step instead of 5%.
     */
    const exerciseNameFromKey = state.oderId.startsWith(`${state.userId}_`)
      ? state.oderId.slice(state.userId.length + 1)
      : state.oderId;
    const isCompound = isCompoundMovement(exerciseNameFromKey);
    const increment = isCompound ? 0.05 : 0.025; // 5% for compounds, 2.5% for isolation
    const rawWeight = state.currentWeight * (1 + increment);
    const newWeight = roundToNearestPlate(rawWeight);

    return {
      newWeight,
      newTargetReps: state.targetRepMin,
      action: 'increase-weight',
      reason: `Hit ${state.targetRepMax} reps on all sets at RPE ${avgRPE.toFixed(1)} — increasing weight to ${newWeight} lbs, resetting to ${state.targetRepMin} reps`,
    };
  }

  // Rule 2: Hit current target but not max → increase reps
  if (allSetsHitTarget && rpeAllowsProgress && state.currentReps < state.targetRepMax) {
    const newReps = state.currentReps + 1;
    return {
      newWeight: state.currentWeight,
      newTargetReps: newReps,
      action: 'increase-reps',
      reason: `Hit ${state.currentReps} reps on all sets at RPE ${avgRPE.toFixed(1)} — increasing target to ${newReps} reps`,
    };
  }

  // Rule 3: Not ready to progress — hold
  return {
    newWeight: state.currentWeight,
    newTargetReps: state.currentReps,
    action: 'hold',
    reason: allSetsHitTarget
      ? `RPE ${avgRPE.toFixed(1)} too high for progression (need ≤ 8)`
      : `Did not hit target of ${state.currentReps} reps on all sets`,
  };
}

// ============================================================================
// STORAGE
// ============================================================================

/**
 * Get the current double progression state for an exercise
 */
export async function getDoubleProgressionState(
  exerciseName: string
): Promise<DoubleProgressionState | null> {
  try {
    const userId = getCurrentUserId();
    if (!userId) return null;

    const key = `${userId}_${exerciseName.toLowerCase()}`;
    const state = await db.doubleProgressionState.get(key);
    return state || null;
  } catch {
    return null;
  }
}

/**
 * Save updated double progression state
 */
export async function saveDoubleProgressionState(
  exerciseName: string,
  weight: number,
  currentReps: number,
  targetRepMin: number,
  targetRepMax: number
): Promise<void> {
  try {
    const userId = getCurrentUserId();
    if (!userId) return;

    const key = `${userId}_${exerciseName.toLowerCase()}`;
    await db.doubleProgressionState.put({
      oderId: key,
      userId,
      currentReps,
      targetRepMin,
      targetRepMax,
      currentWeight: weight,
      lastUpdated: new Date().toISOString(),
    });
  } catch (error) {
    console.error('Failed to save double progression state:', error);
  }
}

/**
 * Apply double progression to a workout exercise.
 * Called from autoProgress as the primary progression method.
 *
 * Returns updated weight and whether progression was applied.
 */
export async function applyDoubleProgression(
  exerciseName: string,
  baseWeight: number,
  targetReps: number,
  completedReps: number[],
  avgRPE: number
): Promise<{ weight: number; reps: number; applied: boolean; reason: string }> {
  const state = await getDoubleProgressionState(exerciseName);

  if (!state) {
    // First time seeing this exercise — initialize state
    const repMin = Math.max(targetReps - 2, 3); // rep range: targetReps-2 to targetReps+2
    const repMax = targetReps + 2;
    await saveDoubleProgressionState(exerciseName, baseWeight, targetReps, repMin, repMax);
    return { weight: baseWeight, reps: targetReps, applied: false, reason: 'Double progression initialized' };
  }

  const result = evaluateDoubleProgression(state, completedReps, avgRPE);

  // Save updated state
  await saveDoubleProgressionState(
    exerciseName,
    result.newWeight,
    result.newTargetReps,
    state.targetRepMin,
    state.targetRepMax
  );

  return {
    weight: result.newWeight,
    reps: result.newTargetReps,
    applied: result.action !== 'hold',
    reason: result.reason,
  };
}
